import { KeyValuePipe, NgClass, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonComponent } from '@examdojo/core/button';
import { CopyToClipboardDirective } from '@examdojo/core/copy-to-clipboard';
import { IconComponent } from '@examdojo/core/icon';
import { FieldDefinition, Procedure, Representation, REPRESENTATION_CATEGORIES } from '@examdojo/core/ontology';
import { SingleSelectComponent } from '@examdojo/core/select';
import { NumberInputComponent, TextInputComponent } from '@examdojo/ui/input';
import { MarkdownModule } from 'ngx-markdown';
import { FieldTypeService, TypeBadgeComponent } from '../../shared';

interface SortedFieldData {
  key: string;
  def: FieldDefinition;
  isOptional: boolean;
}

@Component({
  selector: 'exam-representation-details',
  standalone: true,
  imports: [
    KeyValuePipe,
    NgClass,
    TitleCasePipe,
    MarkdownModule,
    MatMenuModule,
    MatTabsModule,
    MatTooltipModule,
    ReactiveFormsModule,
    ButtonComponent,
    CopyToClipboardDirective,
    IconComponent,
    SingleSelectComponent,
    NumberInputComponent,
    TextInputComponent,
    TypeBadgeComponent,
  ],
  templateUrl: './representation-details.component.html',
  styleUrls: ['./representation-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepresentationDetailsComponent {
  readonly fieldTypeService = inject(FieldTypeService);

  readonly data = input.required<Representation>();
  readonly isRoot = input(false);
  readonly runtimeValue = input<Record<string, unknown> | null>(null);
  readonly rootRepresentationIds = input<Set<string>>(new Set());
  readonly sourceProcedures = input<Procedure[]>([]);
  readonly runtimeState = input<Record<string, unknown>>({});
  readonly representationsMap = input<Map<string, Representation>>(new Map());

  readonly execute = output<unknown>();
  readonly navigateToNode = output<string>();
  readonly autoExecuteRoot = output<void>();

  readonly form = signal<FormGroup>(new FormGroup({}));
  readonly selectedProcedureIndex = signal(0);

  readonly katexOptions = this.fieldTypeService.katexOptions;
  readonly booleanOptions = this.fieldTypeService.booleanOptions;
  readonly representationCategories = REPRESENTATION_CATEGORIES;

  readonly hasMultipleProcedures = computed(() => this.sourceProcedures().length > 1);

  readonly selectedProcedure = computed(() => {
    const procedures = this.sourceProcedures();
    const index = this.selectedProcedureIndex();
    return procedures[index] ?? null;
  });

  readonly rootValues = computed<Record<string, unknown> | null>(() => {
    const procedure = this.selectedProcedure();
    const runtime = this.runtimeState();
    const reprsMap = this.representationsMap();

    if (procedure) {
      for (const inputId of procedure.input_ids) {
        const inputRepr = reprsMap.get(inputId);
        if (inputRepr && runtime[inputId]) {
          const val = runtime[inputId];
          if (val && typeof val === 'object') {
            return val as Record<string, unknown>;
          }
        }
      }
    }

    return null;
  });

  readonly rootModel = computed<Record<string, FieldDefinition> | null>(() => {
    const procedure = this.selectedProcedure();
    const reprsMap = this.representationsMap();

    if (procedure) {
      for (const inputId of procedure.input_ids) {
        const inputRepr = reprsMap.get(inputId);
        if (inputRepr?.model) {
          return inputRepr.model;
        }
      }
    }

    return null;
  });

  readonly inputRepresentationsWithValues = computed(() => {
    const procedure = this.selectedProcedure();
    const runtime = this.runtimeState();
    const reprsMap = this.representationsMap();

    if (!procedure) {
      return [];
    }

    return procedure.input_ids
      .map((inputId) => {
        const repr = reprsMap.get(inputId);
        const values = runtime[inputId] as Record<string, unknown> | undefined;

        if (!repr || !values) {
          return null;
        }

        return {
          id: inputId,
          name: repr.name ?? inputId,
          svgPath: this.fieldTypeService.getCategorySvgPath(repr.category),
          values,
          model: repr.model,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  });

  readonly hasSingleInputRepresentation = computed(() => this.inputRepresentationsWithValues().length === 1);
  readonly singleInputRepresentationWithValues = computed(() => this.inputRepresentationsWithValues()[0]);

  readonly sortedFields = computed<SortedFieldData[]>(() => {
    const model = this.data().model;
    if (!model) {
      return [];
    }
    return Object.entries(model)
      .map(([key, def]) => ({
        key,
        def,
        isOptional: !!def.optional,
      }))
      .sort((a, b) => {
        const pA = a.isOptional ? 0 : 1;
        const pB = b.isOptional ? 0 : 1;
        return pB - pA;
      });
  });

  readonly categorySvgPath = computed(() => this.fieldTypeService.getCategorySvgPath(this.data().category));

  readonly renderedTemplate = computed(() => {
    const values = this.runtimeValue();
    const template = this.data().template;
    const model = this.data().model;
    const type = this.data().type;

    if (!values) {
      return '';
    }

    const formatValue = (val: unknown, fieldDef?: unknown): string => {
      if (Array.isArray(val)) {
        const def = fieldDef as Record<string, unknown> | undefined;
        const isLatexArray =
          def?.['type'] === 'array' &&
          typeof def?.['items'] === 'object' &&
          (def['items'] as Record<string, unknown>)?.['format'] === 'latex';
        if (isLatexArray) {
          return val.map((v) => `$${String(v)}$`).join(', ');
        }
        return val.map((v) => String(v)).join(', ');
      }
      return String(val);
    };

    if (!template && model) {
      const firstLatexField = Object.entries(model).find(
        ([, def]) => def.type === 'string' && 'format' in def && def.format === 'latex',
      );
      if (firstLatexField) {
        const [key] = firstLatexField;
        const val = values[key];
        if (val) {
          return type === 'symbolic' ? `$${String(val)}$` : String(val);
        }
      }

      const firstLatexArrayField = Object.entries(model).find(([, def]) => {
        if (def.type !== 'array') {
          return false;
        }
        const items = (def as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
        return items?.['format'] === 'latex';
      });
      if (firstLatexArrayField) {
        const [key] = firstLatexArrayField;
        const val = values[key];
        if (Array.isArray(val) && val.length > 0) {
          return val.map((v) => `$${String(v)}$`).join(', ');
        }
      }

      if (type === 'numerical' && 'value' in values) {
        return `$${String(values['value'])}$`;
      }
      if (type === 'state' && 'state' in values) {
        const desc = 'description' in values ? `: ${String(values['description'])}` : '';
        return `**${String(values['state'])}**${desc}`;
      }
      return '';
    }

    if (!template) {
      return '';
    }

    const rendered = template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      const val = values[key] ?? '';
      const fieldDef = model?.[key];
      return formatValue(val, fieldDef);
    });
    if (type === 'numerical') {
      return `$${rendered}$`;
    }
    return rendered;
  });

  readonly formattedRuntimeValues = computed(() => {
    const runtime = this.runtimeValue();
    const model = this.data().model;
    if (!runtime || !model) {
      return new Map<string, string>();
    }
    const formatted = new Map<string, string>();
    for (const key of Object.keys(runtime)) {
      const value = runtime[key];
      const field = model[key];

      if (field && field.type === 'string' && 'format' in field && field.format === 'latex') {
        const strVal = String(value);
        formatted.set(key, strVal.startsWith('$') ? strVal : `$${strVal}$`);
      } else if (field && field.type === 'array' && Array.isArray(value)) {
        const items = (field as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
        const isLatexArray = items?.['format'] === 'latex';
        if (isLatexArray) {
          formatted.set(key, value.map((v) => `$${String(v)}$`).join(', '));
        } else {
          formatted.set(key, value.map((v) => String(v)).join(', '));
        }
      } else {
        formatted.set(key, String(value));
      }
    }
    return formatted;
  });

  readonly enumFields = computed(() => {
    const model = this.data().model;
    if (!model) {
      return new Set<string>();
    }
    return new Set(
      Object.entries(model)
        .filter(([, def]) => def.type === 'enum')
        .map(([key]) => key),
    );
  });

  readonly formControls = computed(() => {
    const currentForm = this.form();
    const controls = new Map<string, FormControl>();
    for (const field of this.sortedFields()) {
      const ctrl = currentForm.get(field.key) as FormControl;
      if (ctrl) {
        controls.set(field.key, ctrl);
      }
    }
    return controls;
  });

  private readonly dataChangeEffect = effect(() => {
    this.data();
    this.buildForm();
  });

  buildForm(): void {
    const data = this.data();
    const group: Record<string, FormControl> = {};
    const model = data.model ?? {};

    for (const key in model) {
      const field = model[key];
      if (field.type === 'constant') {
        continue;
      }

      const validators = [Validators.required];
      const value = data.example_data?.[key] ?? null;

      if (field.type === 'number') {
        if (field.min !== undefined) {
          if (field.min_exclusive) {
            validators.push((control) => {
              const val = control.value;
              if (val !== null && val !== undefined && val <= field.min!) {
                return { minExclusive: { min: field.min, actual: val } };
              }
              return null;
            });
          } else {
            validators.push(Validators.min(field.min));
          }
        }
        if (field.max !== undefined) {
          if (field.max_exclusive) {
            validators.push((control) => {
              const val = control.value;
              if (val !== null && val !== undefined && val >= field.max!) {
                return { maxExclusive: { max: field.max, actual: val } };
              }
              return null;
            });
          } else {
            validators.push(Validators.max(field.max));
          }
        }

        if (field.domain === 'natural') {
          validators.push(Validators.pattern(/^[0-9]+$/));
          if (field.min === undefined) {
            validators.push(Validators.min(1));
          }
        } else if (field.domain === 'integer') {
          validators.push(Validators.pattern(/^-?[0-9]+$/));
        }
      }

      group[key] = new FormControl(value, validators);
    }
    this.form.set(new FormGroup(group));
  }

  onExecute(): void {
    const formValue = this.form();
    if (formValue.valid) {
      this.execute.emit(formValue.value);
    }
  }

  onNavigateToInput(): void {
    const procedure = this.selectedProcedure();

    if (procedure && procedure.input_ids.length > 0) {
      this.navigateToNode.emit(procedure.input_ids[0]);
      return;
    }

    const rootIds = this.rootRepresentationIds();
    if (rootIds.size > 0) {
      const rootId = rootIds.values().next().value;
      if (rootId) {
        this.navigateToNode.emit(rootId);
      }
    }
  }

  onTabChange(index: number): void {
    this.selectedProcedureIndex.set(index);
  }

  getFieldLabel(key: string): string {
    const model = this.rootModel();
    if (model && model[key]?.label) {
      return model[key].label;
    }
    return key;
  }

  getFieldLabelFromModel(key: string, model: Record<string, FieldDefinition> | undefined): string {
    if (model && model[key]?.label) {
      return model[key].label;
    }
    return key;
  }
}
